import { Component, OnInit, ChangeDetectorRef } from '@angular/core';

import { ContractsService } from '../../../services/contracts.service';
import { Contract } from '../../../interfaces/contract.interface';
import { ContractFilter } from '../../../interfaces/contract-filter.interface';
import { ContractsListTableComponent } from '../contracts-list-table/contracts-list-table.component';
import { SharedFiltersComponent } from '../../../shared/components';

@Component({
  selector: 'app-contracts-list-wrapper',
  imports: [
    ContractsListTableComponent,
    SharedFiltersComponent
],
  templateUrl: './contracts-list-wrapper.component.html',
  styleUrl: './contracts-list-wrapper.component.scss'
})
export class ContractsListWrapperComponent implements OnInit {
  contracts: Contract[] = [];
  filteredContracts: Contract[] = [];
  loading = true;
  error: string | null = null;

  // Filter state
  currentFilter: ContractFilter = {
    searchTerm: '',
    statusFilter: [],
    biztositoFilter: [],
    assigneeFilter: [],
    dateFromFilter: null,
    dateToFilter: null,
    productFilter: [],
    paymentFrequencyFilter: [],
    currencyFilter: [],
    policyNumberFilter: ''
  };

  constructor(
    private contractsService: ContractsService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.loadContracts();
  }

  loadContracts() {
    this.loading = true;
    this.error = null;
    
    this.contractsService.getContracts().subscribe({
      next: (contracts) => {
        this.contracts = contracts;
        this.filteredContracts = [...contracts]; // Initialize filtered contracts
        this.loading = false;
        
        // Manually trigger change detection
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.error = 'Hiba történt a szerződések betöltése során.';
        this.loading = false;
      }
    });
  }

  // Filter methods
  onFilterChange(filter: ContractFilter) {
    this.currentFilter = { ...filter };
    this.applyFilters();
  }

  onClearFilters() {
    this.currentFilter = {
      searchTerm: '',
      statusFilter: [],
      biztositoFilter: [],
      assigneeFilter: [],
      dateFromFilter: null,
      dateToFilter: null,
      productFilter: [],
      paymentFrequencyFilter: [],
      currencyFilter: [],
      policyNumberFilter: ''
    };
    this.filteredContracts = [...this.contracts];
  }

  private applyFilters() {
    this.filteredContracts = this.contracts.filter(data => {
      // Text search
      const searchMatch = this.currentFilter.searchTerm ? 
        [
          data.insurancecompanyname,
          data.policynumber,
          data.paymentfrequency,
          data.productname,
          data.startdate?.toString(),
          data.policyholdercustomername,
          data.policyholdercompanyname,
          data.insuredcustomername,
          data.insuredcompanyname,
          data.status,
          data.currency,
          data.insurancepremium?.toString(),
          data.annualpremium?.toString(),
          data.dayofarrears?.toString(),
          data.productcode
        ].join(' ').toLowerCase().includes(this.currentFilter.searchTerm.toLowerCase()) : true;

      // Status filter - multi-select support
      const statusMatch = Array.isArray(this.currentFilter.statusFilter) 
        ? (this.currentFilter.statusFilter.length === 0 || (data.status ? this.currentFilter.statusFilter.includes(data.status) : false))
        : (this.currentFilter.statusFilter === 'all' || data.status === this.currentFilter.statusFilter);

      // Biztosító filter - multi-select support
      const biztositoMatch = Array.isArray(this.currentFilter.biztositoFilter)
        ? (this.currentFilter.biztositoFilter.length === 0 || (data.insurancecompanyname ? this.currentFilter.biztositoFilter.includes(data.insurancecompanyname) : false))
        : (this.currentFilter.biztositoFilter === 'all' || data.insurancecompanyname === this.currentFilter.biztositoFilter);

      // Date filter
      let dateMatch = true;
      if (this.currentFilter.dateFromFilter || this.currentFilter.dateToFilter) {
        const itemDate = new Date(data.startdate);
        if (this.currentFilter.dateFromFilter && itemDate < this.currentFilter.dateFromFilter) {
          dateMatch = false;
        }
        if (this.currentFilter.dateToFilter && itemDate > this.currentFilter.dateToFilter) {
          dateMatch = false;
        }
      }

      // Advanced filters
      // Product filter - multi-select support
      const productMatch = Array.isArray(this.currentFilter.productFilter)
        ? (this.currentFilter.productFilter.length === 0 || (data.productname ? this.currentFilter.productFilter.includes(data.productname) : false))
        : (this.currentFilter.productFilter === 'all' || data.productname === this.currentFilter.productFilter);

      // Payment frequency filter - multi-select support
      const paymentFrequencyMatch = Array.isArray(this.currentFilter.paymentFrequencyFilter)
        ? (this.currentFilter.paymentFrequencyFilter.length === 0 || (data.paymentfrequency ? this.currentFilter.paymentFrequencyFilter.includes(data.paymentfrequency) : false))
        : (this.currentFilter.paymentFrequencyFilter === 'all' || data.paymentfrequency === this.currentFilter.paymentFrequencyFilter);

      // Currency filter - multi-select support
      const currencyMatch = Array.isArray(this.currentFilter.currencyFilter)
        ? (this.currentFilter.currencyFilter.length === 0 || (data.currency ? this.currentFilter.currencyFilter.includes(data.currency) : false))
        : (this.currentFilter.currencyFilter === 'all' || data.currency === this.currentFilter.currencyFilter);

      // Policy number filter
      const policyNumberMatch = this.currentFilter.policyNumberFilter ? 
        (data.policynumber?.toLowerCase().includes(this.currentFilter.policyNumberFilter.toLowerCase()) ?? false) : true;

      return searchMatch && statusMatch && biztositoMatch && dateMatch && 
             productMatch && paymentFrequencyMatch && currencyMatch && 
             policyNumberMatch;
    });
  }
}